import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { seo } from "@utils/seo";
import i18n from "@modules/shared/localization/i18n";
import { PageBreadcrumb } from "@modules/shared/Breadcrumbs/PageBreadcrumb";
import { getPriceListFiles } from "@modules/shop/priceList";
import type { PriceListFile } from "@modules/shop/priceList";

export const Route = createFileRoute(
  "/$locale/_layout/(public)/_public/info/price-list",
)({
  component: PriceListPage,
  ssr: true,
  loader: () => getPriceListFiles(),
  // A new file appears once a day; keep the list fresh within minutes.
  headers: () => ({
    "Cache-Control":
      "public, max-age=300, s-maxage=300, stale-while-revalidate=3600",
  }),
  head: ({ params }) => {
    const t = i18n.getFixedT(params.locale);
    return {
      meta: [
        ...seo({
          title: t("Seo:priceList.title"),
          description: t("Seo:priceList.description"),
        }),
      ],
    };
  },
});

function PriceListPage() {
  const { t } = useTranslation();
  const { locale } = Route.useParams();
  const files = Route.useLoaderData();
  const [current, ...archive] = files;

  return (
    <article className="col-[10/39] py-[60px] max-mobile:col-span-padded max-mobile:py-10 laptop:col-[10/36]">
      <PageBreadcrumb
        className="max-mobile:hidden"
        segments={[
          {
            label: t("StaticPages.breadcrumbs.home"),
            to: "/$locale",
            params: { locale },
          },
          { label: t("PriceList.title") },
        ]}
      />

      <h1 className="mt-10 max-w-[22ch] text-3xlarge font-bold break-words hyphens-auto text-balance text-text-1 max-mobile:mt-0 max-mobile:text-2xlarge">
        {t("PriceList.title")}
      </h1>

      <div className="mt-10 max-w-[46rem] text-pretty max-mobile:mt-7">
        <p className="text-text-2">{t("PriceList.intro")}</p>

        {files.length > 0 ? (
          <>
            <h2 className="mt-10 text-large font-bold text-text-1">
              {t("PriceList.current")}
            </h2>
            <PriceListLink file={current} locale={locale} />

            {archive.length > 0 && (
              <>
                <h2 className="mt-10 text-large font-bold text-text-1">
                  {t("PriceList.archive")}
                </h2>
                <ul className="mt-2 divide-y divide-border-5">
                  {archive.map((file) => (
                    <li key={file.name}>
                      <PriceListLink file={file} locale={locale} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        ) : (
          <p className="mt-10 text-text-3">{t("PriceList.empty")}</p>
        )}
      </div>
    </article>
  );
}

function PriceListLink({
  file,
  locale,
}: Readonly<{ file: PriceListFile; locale: string }>) {
  const { t } = useTranslation();
  const date = new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Europe/Zagreb",
  }).format(new Date(file.publishedAt));

  return (
    <a
      href={`/cjenik/${encodeURIComponent(file.name)}`}
      download={file.name}
      className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-3 text-text-1 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <span className="min-w-0 break-all">{file.name}</span>
      <span className="text-small text-text-3">
        {t("PriceList.published", { date })} · {t("PriceList.download")}
      </span>
    </a>
  );
}
